import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, IconButton, Typography, Box, TextField, InputAdornment } from '@mui/material';
import { ModalClose } from '../../assets/icons';
import 'react-international-phone/style.css';
import { decodeIdToken, decodeMetadataToken } from '../../utils/token.utils';
import ChargebeeService from '../../services/chargebee.service';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { config } from '../../config';
import { PRICING_GHLEvent } from '../../components/subscription/pricing/pricingData';

interface ConfirmCardPaymentProps {
  open: boolean;
  onClose: () => void;
  planDetails: {
    planName: string;
    units: number;
    amount: number;
    frequency: string;
  };
  selectedPlan: string;
  onBuyNow: (couponCode?: string, isGHLEvent?: boolean) => Promise<void>;
  isSaleLive?: boolean;
}

const ConfirmCardPayment: React.FC<ConfirmCardPaymentProps> = ({ open, onClose, planDetails, selectedPlan, onBuyNow, isSaleLive = false }) => {
  const userEmail = decodeIdToken()?.email || 'No email found';
  const [cardDetails, setCardDetails] = useState({ last4: '', expiryMonth: '', expiryYear: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingCardDetails, setIsFetchingCardDetails] = useState(false);
  const [isGHLEvent, setIsGHLEvent] = useState(false);


  // Coupon state
  const [showPromoField, setShowPromoField] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<{
    code: string;
    discount: number;
    type: string;
  } | null>(null);


  useEffect(() => {
    const token = decodeMetadataToken();
    const planId = token?.companies?.[0]?.subscription?.planId;
    const isGHL = (planId?.toLowerCase())?.includes('agency') || (planId?.toLowerCase())?.includes('creators');
    setIsGHLEvent(isGHL);
  }, []);

  useEffect(() => {
    const fetchBillingDetails = async () => {
      try {
        setIsFetchingCardDetails(true);
        const metadata = decodeMetadataToken();
        const customerId = metadata?.companies?.[0]?.id;
        
        if (customerId) {
          const response = await ChargebeeService.getBillingDetails(customerId);
          const paymentSources = response.data?.paymentMethods;
          const paymentSource = paymentSources.filter((card: any) => card?.isPrimary)[0] || paymentSources[0];
          if (paymentSource) {
            setCardDetails({
              last4: paymentSource.card?.last4 || '',
              expiryMonth: paymentSource.card?.expiryMonth || '',
              expiryYear: paymentSource.card?.expiryYear?.toString().slice(-2) || '',
            });
          }
        }
      } catch (error) {
        toast.error('Failed to fetch billing details');
        console.error('Failed to fetch billing details:', error);
      } finally {
        setIsFetchingCardDetails(false);
      }
    };

    if (open) {
      fetchBillingDetails();
    }
  }, [open]);

  // Auto-apply coupon code when sale is live
  useEffect(() => {
    const autoApplySaleCoupon = async () => {
      if (isSaleLive && open && !appliedPromo) {
        const couponCode = planDetails.frequency.toLowerCase() === 'month' 
          ? config.monthlyCouponCode 
          : config.yearlyCouponCode;
        
        if (couponCode) {
          try {
            // Validate the coupon to get actual discount details
            const response = await ChargebeeService.validateCoupon(
              couponCode,
              planDetails.planName
            );

            if (response.data.valid) {
              const { coupon } = response.data;
              setAppliedPromo({
                code: coupon.id,
                discount: coupon.discountPercentage || coupon.discountAmount,
                type: coupon.discountType
              });
              toast.success(`Sale coupon ${couponCode} applied automatically!`);
            } else {
              console.warn(`Sale coupon ${couponCode} is not valid for this plan`);
            }
          } catch (error) {
            console.error('Error auto-applying sale coupon:', error);
            // Still apply the coupon code even if validation fails
            setAppliedPromo({
              code: couponCode,
              discount: 0,
              type: 'percentage'
            });
            toast.success(`Sale coupon ${couponCode} applied automatically!`);
          }
        }
      }
    };

    autoApplySaleCoupon();
  }, [isSaleLive, open, planDetails.frequency, appliedPromo, planDetails.planName]);

  const handleClose = () => {
    onClose();
  };

  const handleBuyNow = async () => {
    setIsLoading(true);
    try {
      await onBuyNow(appliedPromo?.code, isGHLEvent);
      handleClose();
    } catch (error) {
      console.error('Failed to process purchase:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateCoupon = async () => {
    if (!promoCode.trim()) {
      setPromoError('Please enter a promo code');
      toast.error('Please enter a promo code');
      return;
    }

    try {
      const response = await ChargebeeService.validateCoupon(
        promoCode,
        planDetails.planName
      );

      if (response.data.valid) {
        const { coupon } = response.data;
        setAppliedPromo({
          code: coupon.id,
          discount: coupon.discountPercentage || coupon.discountAmount,
          type: coupon.discountType
        });
        setPromoError('');
        setShowPromoField(false);
        toast.success('Promo code applied successfully!');
      } else {
        setPromoError(response.data.error || 'Invalid promo code');
        setAppliedPromo(null);
        toast.error(response.data.error || 'Invalid promo code');
      }
    } catch (error: any) {
      console.error('Error validating coupon:', error);
      setPromoError('Failed to validate promo code');
      setAppliedPromo(null);
      toast.error('Failed to validate promo code. Please try again.');
    }
  };


  const getDisplayAmount = () => {
    if (isGHLEvent) {
      const isYearly = planDetails.frequency.toLowerCase() === 'year';
      const units = planDetails.units.toString();
      
      // Check in events category (50, 200, 500)
      if (PRICING_GHLEvent.events[units as keyof typeof PRICING_GHLEvent.events]) {
        const monthlyAmount = PRICING_GHLEvent.events[units as keyof typeof PRICING_GHLEvent.events].monthly;
        return (isYearly ? monthlyAmount * 12 : monthlyAmount) * 0.35;
      }
      
      // Check in funnels category (3000, 6000, 10000)
      if (PRICING_GHLEvent.funnels[units as keyof typeof PRICING_GHLEvent.funnels]) {
        const monthlyAmount = PRICING_GHLEvent.funnels[units as keyof typeof PRICING_GHLEvent.funnels].monthly;
        return (isYearly ? monthlyAmount * 12 : monthlyAmount) * 0.35;
      }
    }
    return planDetails.amount;
  };

  // Calculate discounted price if a promo is applied
  const getDiscountedPrice = () => {
    const displayAmount = getDisplayAmount();
    if (!appliedPromo) return displayAmount;
    if (appliedPromo.type === 'percentage') {
      return displayAmount * (1 - appliedPromo.discount / 100);
    } else if (appliedPromo.type === 'fixed_amount') {
      return Math.max(0, displayAmount - (appliedPromo.discount / 100));
    }
    
    return planDetails.amount;
  };

  const discountedPrice = getDiscountedPrice();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className='card-details-modal'
      sx={{ '& .MuiDialog-paper': { width: '100%', maxWidth: '775px', borderRadius: '16px', border: '1px solid #E8E8EA' } }}
    >
      <DialogTitle sx={{ m: 0, }} p={{ xs: "16px 16px 15px !important" }}>
        {/* <Typography color='#333333' fontSize="min(24px, calc(20px + 4 * ((100vw - 320px) / (1920 - 320))))" fontWeight='500'>Add Workspace</Typography> */}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={() => ({
            position: 'absolute',
            right: { xs: "17px" },
            top: { xs: "15px" },
          })}
        >
          <ModalClose></ModalClose>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderTop: 'none', borderBottom: 'none', p: { xs: "16px", sm: "25px 32px 32px" } }}>
        <Typography variant="body2" color="#232B2F" mb="4px">Summary</Typography>

        <Box borderRadius="8px" border="1px solid #C8D3DA" p="12px" mb="16px">
          <Box display="flex" justifyContent="space-between" mb="16px" px={{ xs: "0", sm: "16px" }} flexWrap="wrap">
            <Box>
              <Typography variant="body2" color="#1976d2" lineHeight="1.7" fontWeight="600">
                {planDetails.planName}
              </Typography>
              <Typography variant="body2" color="#4D5B66" lineHeight="1.7" fontWeight="500">
                {planDetails.units} leads
              </Typography>
            </Box>
            <Typography variant="body2" color="#4D5B66" fontWeight="600">
              ${getDisplayAmount().toFixed(2)}/ {planDetails.frequency}
            </Typography>
          </Box>

          {/* Coupon code section */}
          {!isGHLEvent &&
            <Box display="flex" justifyContent="flex-end" flexDirection="column" mb="16px">
              {!showPromoField ? (
                <>
                  {appliedPromo ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '4px 8px',
                        backgroundColor: '#F0FFF4',
                        borderRadius: '4px',
                        width: 'fit-content',
                        marginLeft: 'auto',
                        mr: '16px'
                      }}
                    >
                      <Typography color="#1D9652" fontSize="14px">
                        {appliedPromo.code} applied
                      </Typography>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setAppliedPromo(null);
                          setPromoCode('');
                        }}
                      >
                        <path d="M8 1C4.1 1 1 4.1 1 8C1 11.9 4.1 15 8 15C11.9 15 15 11.9 15 8C15 4.1 11.9 1 8 1ZM10.7 11.5L8 8.8L5.3 11.5L4.5 10.7L7.2 8L4.5 5.3L5.3 4.5L8 7.2L10.7 4.5L11.5 5.3L8.8 8L11.5 10.7L10.7 11.5Z" fill="#1D9652" />
                      </svg>
                    </Box>
                  ) : (
                    <Typography
                      color="#2B2B30"
                      fontSize="14px"
                      display="flex"
                      justifyContent="flex-end"
                      mr="16px"
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                      onClick={() => setShowPromoField(true)}
                    >
                      Have a promo code?
                    </Typography>
                  )}
                </>
              ) : (
                <Box px={{ xs: "0", sm: "16px" }}>
                  <TextField
                    variant="outlined"
                    placeholder="Promo Code"
                    size="small"
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e.target.value);
                      setPromoError('');
                    }}
                    error={!!promoError}
                    helperText={promoError}
                    sx={{
                      width: "100%",
                      marginBottom: promoError ? "0" : "8px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: '3px',
                        borderColor: '#C7C7C7',
                        background: '#ffffff'
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: '#333333',
                        border: '1px solid'
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Box display="flex" justifyContent="center" alignItems="center">
                            <Box
                              sx={{
                                background: '#178EFC',
                                borderRadius: '4px',
                                padding: '4px 10px',
                                marginRight: '4px',
                                minWidth: '57px',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#fff',
                                cursor: 'pointer'
                              }}
                              onClick={validateCoupon}
                            >
                              Apply
                            </Box>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="17"
                              height="16"
                              viewBox="0 0 17 16"
                              fill="none"
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                setShowPromoField(false);
                                setPromoError('');
                              }}
                            >
                              <path d="M8.07324 1C4.17324 1 1.07324 4.1v 1.07324 8C1.07324 11.9 4.17324 15 8.07324 15C11.9732 15 15.0732 11.9 15.0732 8C15.0732 4.1 11.9732 1 8.07324 1ZM10.7732 11.5L8.07324 8.8L5.37324 11.5L4.57324 10.7L7.27324 8L4.57324 5.3L5.37324 4.5L8.07324 7.2L10.7732 4.5L11.5732 5.3L8.87324 8L11.5732 10.7L10.7732 11.5Z" fill="#A3A3A3" />
                            </svg>
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              )}
            </Box>
          }

          <Box display="flex" justifyContent="space-between" alignItems="center" p="8px 16px" bgcolor="#F0F4FF" borderRadius="8px">
            <Typography variant="body2" color="#4D5B66">
              Total/ {planDetails.frequency}
            </Typography>
            <Box>
              {appliedPromo && (
                <Typography variant="body2" color="#666666" sx={{ textDecoration: 'line-through', mr: 1, display: 'inline-block' }}>
                  ${planDetails.amount.toFixed(2)}
                </Typography>
              )}
              <Typography variant="body2" color={appliedPromo ? "#22B060" : "#333333"} fontWeight="600" display="inline-block">
                ${discountedPrice.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box borderRadius="8px" border="1px solid #C8D3DA" p="12px">
          <Box display="flex" justifyContent="space-between" mb="8px" borderBottom="1px solid #E8EAF0" pb="8px" px={{ xs: "0", sm: "16px" }}>
            <Box>
              <Typography variant="body2" color="#666666" lineHeight="1.7" mb="4px">Billing Email</Typography>
              <Box display="flex" alignItems="center" gap="12px">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M17 20.5H7C4 20.5 2 19 2 15.5V8.5C2 5 4 3.5 7 3.5H17C20 3.5 22 5 22 8.5V15.5C22 19 20 20.5 17 20.5Z" stroke="#666666" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M17 9L13.87 11.5C12.84 12.32 11.15 12.32 10.12 11.5L7 9" stroke="#666666" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <Typography variant="body2" color="#666666" lineHeight="1.7" display="inline-block">{userEmail}</Typography>
              </Box>
            </Box>
          </Box>
          <Box px={{ xs: "0", sm: "16px" }}>
            <Typography variant="body2" color="#666666" lineHeight="1.7" mb="4px">Card Details</Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap">
              <Box display="inline-flex" alignItems="center" gap="12px">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M19.125 4.5H4.875C3.42525 4.5 2.25 5.67525 2.25 7.125V16.875C2.25 18.3247 3.42525 19.5 4.875 19.5H19.125C20.5747 19.5 21.75 18.3247 21.75 16.875V7.125C21.75 5.67525 20.5747 4.5 19.125 4.5Z" stroke="#666666" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M2.25 9H21.75M6 14.0625H8.25V15H6V14.0625Z" stroke="#666666" stroke-width="1.875" stroke-linejoin="round" />
                </svg>
                <Typography variant="body2" color="#666666" lineHeight="1.7">
                  **** **** **** {cardDetails.last4}
                </Typography>
              </Box>
              <Typography variant="body2" color="#666666" lineHeight="1.7" display="inline-block" ml="69px">
                {cardDetails.expiryMonth}/{cardDetails.expiryYear}
              </Typography>
            </Box>
          </Box>
        </Box>

      </DialogContent>
      <DialogActions sx={{ mb: { xs: "16px", sm: "32px" }, mr: { xs: "11px", sm: "32px" }, p: "0" }}>
        <Button onClick={handleClose} size='large' color="primary" sx={{
          border: '1px solid #E8EAF0',
          px: '22px',
          color: '#2B2B30 !important',
          textTransform: 'capitalize',
          borderRadius: '8px',
          mr: '16px',
          '&:hover': {
            border: '1px solid #E8EAF0 !important',
            boxShadow: 'unset !important'
          },
        }}>
          Back
        </Button>
        <Button
          variant='contained'
          size='large'
          color='primary'
          disabled={isLoading || isFetchingCardDetails}
          onClick={handleBuyNow}
          sx={{
            textTransform: 'capitalize',
            color: '#FFFFFF !important',
            px: '24px',
            borderRadius: '8px',
            '&:hover': {
              border: 'none !important'
            },
            minWidth: "94px",
            '&.Mui-disabled': {
              backgroundColor: '#E8EAF0',
              color: '#A3A3A3 !important'
            }
          }}
        >
          {isLoading ? 'Processing...' : isFetchingCardDetails ? 'Loading...' : 'Buy Now'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmCardPayment;
